import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { getUserSession } from '../services/authService';
import {
  buscarInformesPacientes,
  fetchInformesDocumento,
  fetchInformesEspecialidades,
  fetchInformesMenu,
  fetchInformesPendientes,
  fetchInformesReportesDiarios,
  saveInformesDocumento,
} from '../services/informesService';
import InformePlantillaForm from '../informesPlantillas/InformePlantillaForm';
import {
  emptyDatosPlantilla,
  parseDatosInforme,
  ramaDesdeEspecialidad,
  seccionesPlantilla,
  tienePlantillaInforme,
  tituloPlantilla,
} from '../informesPlantillas/plantillas';
import '../styles/informes.css';

const bookingDesdeLocation = (state) => {
  if (!state || typeof state !== 'object') {
    return {};
  }
  if (state.booking && typeof state.booking === 'object') {
    return state.booking;
  }
  if (state.specialist || state.modality || state.location || state.specialty) {
    return {
      modality: state.modality,
      specialist: state.specialist,
      location: state.location,
      specialty: state.specialty,
      slotPagado: state.slotPagado,
      pagoStripe: state.pagoStripe,
      horarioTomado: state.horarioTomado,
      pagoCompletado: state.pagoCompletado,
    };
  }
  return {};
};

const aplicarBasePaciente = (datos, base) => {
  const out = {
    ...(datos || {}),
    datosGenerales: { ...((datos && datos.datosGenerales) || {}) },
  };
  Object.keys(base || {}).forEach((key) => {
    const valor = base[key];
    if (!valor) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(out.datosGenerales, key) && !out.datosGenerales[key]) {
      out.datosGenerales[key] = valor;
    }
    if (Object.prototype.hasOwnProperty.call(out, key) && typeof out[key] === 'string' && !out[key]) {
      out[key] = valor;
    }
  });
  return out;
};

const InformesPage = () => {
  const history = useHistory();
  const location = useLocation();
  const sessionUser = getUserSession();
  const sessionUserId = (sessionUser && sessionUser.id) || null;
  const isEspecialista = Boolean(sessionUser && sessionUser.tipo === 'especialista');

  const [vista, setVista] = useState('buscar'); // buscar | menu | editor | pendientes | terapias | reportes
  const [especialidades, setEspecialidades] = useState([]);
  const [especialidadCodigo, setEspecialidadCodigo] = useState('');
  const [q, setQ] = useState('');
  const [pacientes, setPacientes] = useState([]);
  const [consultado, setConsultado] = useState(false);
  const [menu, setMenu] = useState(null);
  const [estudioId, setEstudioId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [pendientesModo, setPendientesModo] = useState('sin_evaluacion');
  const [terapias, setTerapias] = useState([]);
  const [reportes, setReportes] = useState([]);

  const [editorTipo, setEditorTipo] = useState('');
  const [editorNumero, setEditorNumero] = useState(0);
  const [editorTitulo, setEditorTitulo] = useState('');
  const [editorCuerpo, setEditorCuerpo] = useState('');
  const [editorDatos, setEditorDatos] = useState(null);
  const [editorRama, setEditorRama] = useState('');
  const [editorUsaPlantilla, setEditorUsaPlantilla] = useState(false);

  const especialidadActiva = useMemo(() => {
    if (!especialidadCodigo) {
      return null;
    }
    return especialidades.find((item) => item.codigo === especialidadCodigo) || null;
  }, [especialidades, especialidadCodigo]);

  useEffect(() => {
    if (!isEspecialista || !sessionUserId) {
      return undefined;
    }

    setLoading(true);
    setError('');
    fetchInformesEspecialidades(sessionUserId)
      .then((data) => {
        const lista = (data && data.especialidades) || [];
        setEspecialidades(lista);
        const codigoInicial =
          lista.length > 0 ? lista[0].codigo : especialidadCodigo || '';
        if (codigoInicial) {
          setEspecialidadCodigo(codigoInicial);
        }
        return buscarInformesPacientes(sessionUserId, codigoInicial, '');
      })
      .then((data) => {
        if (!data) {
          return;
        }
        setPacientes((data && data.pacientes) || []);
        setConsultado(true);
      })
      .catch((err) => {
        setError(err.message || 'No se pudieron cargar especialidades.');
        setConsultado(true);
      })
      .finally(() => setLoading(false));

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEspecialista, sessionUserId]);

  useEffect(() => {
    const fromState = location.state && location.state.estudioId;
    if (fromState && isEspecialista && sessionUserId) {
      abrirMenu(Number(fromState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isEspecialista, sessionUserId]);

  const buscar = () => {
    if (!sessionUserId) {
      return;
    }
    setLoading(true);
    setError('');
    setOk('');
    buscarInformesPacientes(sessionUserId, especialidadCodigo, q)
      .then((data) => {
        setPacientes((data && data.pacientes) || []);
        setConsultado(true);
        setVista('buscar');
      })
      .catch((err) => setError(err.message || 'No se pudo buscar.'))
      .finally(() => setLoading(false));
  };

  const abrirMenu = (id) => {
    if (!sessionUserId || !id) {
      return;
    }
    setLoading(true);
    setError('');
    setOk('');
    setEstudioId(id);
    fetchInformesMenu(sessionUserId, id)
      .then((data) => {
        setMenu(data);
        if (data && data.estudio && data.estudio.especialidad_codigo) {
          setEspecialidadCodigo(data.estudio.especialidad_codigo);
        }
        setVista('menu');
      })
      .catch((err) => {
        setMenu(null);
        setError(err.message || 'No se pudo abrir el menú.');
        setVista('buscar');
      })
      .finally(() => setLoading(false));
  };

  const abrirPendientes = (modo) => {
    if (!sessionUserId || !especialidadCodigo) {
      setError('Seleccione un tipo de estudio.');
      return;
    }
    setPendientesModo(modo);
    setLoading(true);
    setError('');
    fetchInformesPendientes(sessionUserId, especialidadCodigo, modo)
      .then((data) => {
        setPacientes((data && data.pacientes) || []);
        setVista('pendientes');
      })
      .catch((err) => setError(err.message || 'No se pudo cargar el listado.'))
      .finally(() => setLoading(false));
  };

  const abrirTerapias = () => {
    if (!menu) {
      return;
    }
    setTerapias((menu.terapias_pagadas) || []);
    setVista('terapias');
  };

  const abrirReportesDiarios = () => {
    if (!sessionUserId || !estudioId) {
      return;
    }
    setLoading(true);
    setError('');
    fetchInformesReportesDiarios(sessionUserId, estudioId)
      .then((data) => {
        setReportes((data && data.reportes) || []);
        setVista('reportes');
      })
      .catch((err) => {
        setReportes([]);
        setVista('reportes');
        setError(err.message || 'No se pudieron cargar los reportes.');
      })
      .finally(() => setLoading(false));
  };

  const abrirEditor = (opcion) => {
    if (!opcion || (!opcion.habilitada && !opcion.existe)) {
      return;
    }
    const tipo = opcion.tipo;
    let numeroLoad = 0;
    let numeroNuevo = 0;

    if (tipo === 'avance') {
      if (opcion.habilitada) {
        numeroNuevo = opcion.siguiente_numero || 1;
        numeroLoad = 0;
      } else {
        numeroLoad = opcion.avances || 1;
        numeroNuevo = numeroLoad;
      }
    }

    const numeroFinal = numeroNuevo || numeroLoad;
    setEditorTipo(tipo);
    setEditorNumero(numeroFinal);
    setEditorTitulo('');
    setEditorCuerpo('');
    setEditorDatos(null);
    setLoading(true);
    setError('');
    setOk('');

    const ramaMenu = ramaDesdeEspecialidad(
      (menu && menu.estudio && menu.estudio.especialidad_codigo) || especialidadCodigo,
      (menu && menu.estudio && menu.estudio.especialidad_etiqueta) || ''
    );

    fetchInformesDocumento(sessionUserId, estudioId, tipo, numeroLoad)
      .then((data) => {
        const rama = (data && data.rama) || ramaMenu;
        const usaPlantilla = Boolean(data && data.plantilla) || tienePlantillaInforme(rama);
        const doc = data && data.documento;
        const base = (data && data.datos_base) || {};
        setEditorRama(rama);
        setEditorUsaPlantilla(usaPlantilla);
        setEditorNumero((doc && doc.numero_avance) || numeroFinal);

        if (usaPlantilla) {
          const datos = aplicarBasePaciente(
            parseDatosInforme(doc && (doc.datos || doc.cuerpo), tipo, rama),
            base
          );
          setEditorDatos(datos);
          setEditorTitulo(
            (doc && doc.titulo) || tituloPlantilla(tipo, rama, numeroFinal)
          );
          setEditorCuerpo('');
        } else {
          setEditorDatos(null);
          if (doc) {
            setEditorTitulo(doc.titulo || '');
            setEditorCuerpo(doc.cuerpo || '');
          } else if (tipo === 'avance') {
            setEditorTitulo(`Informe de avance ${numeroFinal || 1}`);
          } else if (tipo === 'evaluacion_plan') {
            setEditorTitulo('Informe de Evaluación y Plan');
          } else {
            setEditorTitulo('Evaluación Inicial');
          }
        }
        setVista('editor');
      })
      .catch((err) => {
        const usaPlantilla = tienePlantillaInforme(ramaMenu);
        setEditorRama(ramaMenu);
        setEditorUsaPlantilla(usaPlantilla);
        if (usaPlantilla) {
          setEditorDatos(emptyDatosPlantilla(tipo, ramaMenu));
          setEditorTitulo(tituloPlantilla(tipo, ramaMenu, numeroFinal));
        } else if (tipo === 'evaluacion_plan') {
          setEditorTitulo('Informe de Evaluación y Plan');
        } else if (tipo === 'avance') {
          setEditorTitulo(`Informe de avance ${numeroFinal || 1}`);
        } else {
          setEditorTitulo('Evaluación Inicial');
        }
        setVista('editor');
        setError(err.message || 'No se pudo cargar el informe guardado. Puede escribir uno nuevo.');
      })
      .finally(() => setLoading(false));
  };

  const guardarEditor = () => {
    if (!sessionUserId || !estudioId || !editorTipo) {
      return;
    }
    setLoading(true);
    setError('');
    setOk('');
    saveInformesDocumento({
      solicitante_id: sessionUserId,
      estudio_id: estudioId,
      tipo: editorTipo,
      titulo: editorTitulo,
      cuerpo: editorUsaPlantilla ? JSON.stringify(editorDatos || {}) : editorCuerpo,
      datos: editorUsaPlantilla ? editorDatos : undefined,
      numero_avance: editorTipo === 'avance' ? editorNumero : 0,
    })
      .then(() => {
        setOk('Informe guardado.');
        return fetchInformesMenu(sessionUserId, estudioId);
      })
      .then((data) => {
        setMenu(data);
        setVista('menu');
      })
      .catch((err) => setError(err.message || 'No se pudo guardar.'))
      .finally(() => setLoading(false));
  };

  if (!sessionUserId) {
    return (
      <div className="informes-root">
        <div className="informes-shell">
          <p className="informes-muted">Debe iniciar sesión.</p>
          <Link className="informes-back" to="/iniciar-sesion">
            Ir a iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  if (!isEspecialista) {
    return (
      <div className="informes-root">
        <div className="informes-shell">
          <p className="informes-muted">Informes solo está disponible para especialistas.</p>
          <Link className="informes-back" to="/">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="informes-root">
      <div className="informes-shell">
        <button
          type="button"
          className="informes-back"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          onClick={() => {
            if (vista === 'menu' || vista === 'buscar') {
              history.push({
                pathname: '/agenda/calendario',
                state: bookingDesdeLocation(location.state),
              });
            } else {
              setVista(estudioId ? 'menu' : 'buscar');
              setError('');
              setOk('');
            }
          }}
        >
          ← {vista === 'menu' || vista === 'buscar' ? 'Volver al calendario' : 'Menú principal'}
        </button>

        <h1>Reportes Informes</h1>
        <p className="informes-muted">
          Documentos clínicos del estudio Beyond (paciente + especialidad con usted). El reporte corto
          de cada cita sigue en el calendario.
        </p>

        {error ? <p className="informes-alert informes-alert--error">{error}</p> : null}
        {ok ? <p className="informes-alert informes-alert--ok">{ok}</p> : null}
        {loading ? <p className="informes-muted">Cargando…</p> : null}

        {vista === 'buscar' || vista === 'pendientes' ? (
          <>
            {especialidades.length > 1 ? (
              <label className="informes-field">
                <span>Tipo de estudio</span>
                <select
                  value={especialidadCodigo}
                  onChange={(event) => setEspecialidadCodigo(event.target.value)}
                >
                  {especialidades.map((item) => (
                    <option key={item.codigo} value={item.codigo}>
                      {item.etiqueta || item.codigo}
                    </option>
                  ))}
                </select>
              </label>
            ) : especialidadActiva ? (
              <p className="informes-muted">
                Tipo de estudio: <strong>{especialidadActiva.etiqueta || especialidadActiva.codigo}</strong>
              </p>
            ) : (
              <p className="informes-alert informes-alert--warn">
                Aún no hay estudios Beyond. Se crean al pagar la primera cita de una especialidad.
              </p>
            )}

            {vista === 'buscar' ? (
              <>
                <label className="informes-field">
                  <span>Nombre paciente</span>
                  <input
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    placeholder="Ej. Lucas"
                  />
                </label>
                <div className="informes-actions">
                  <button type="button" className="informes-btn informes-btn--primary" onClick={buscar}>
                    Consultar
                  </button>
                  <button
                    type="button"
                    className="informes-btn informes-btn--secondary"
                    onClick={() => abrirPendientes('sin_evaluacion')}
                  >
                    Pacientes sin Evaluación Inicial
                  </button>
                  <button
                    type="button"
                    className="informes-btn informes-btn--secondary"
                    onClick={() => abrirPendientes('sin_plan')}
                  >
                    Pacientes sin Informe Evaluación y Plan
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>
                  {pendientesModo === 'sin_plan'
                    ? 'Pacientes sin Informe Evaluación y Plan'
                    : 'Pacientes sin Evaluación Inicial'}
                </h2>
                <div className="informes-actions">
                  <button
                    type="button"
                    className="informes-btn informes-btn--ghost"
                    onClick={() => setVista('buscar')}
                  >
                    Otra consulta
                  </button>
                </div>
              </>
            )}

            <ul className="informes-list">
              {pacientes.map((item) => (
                <li key={item.estudio_id}>
                  <button type="button" onClick={() => abrirMenu(item.estudio_id)}>
                    {item.paciente_nombre || 'Paciente'}
                    <span className="informes-list-meta">
                      {item.especialidad_etiqueta || item.especialidad_codigo}
                      {item.dias_semana ? ` · ${item.dias_semana}` : ''}
                      {typeof item.citas_pagadas === 'number'
                        ? ` · ${item.citas_pagadas} citas pagadas`
                        : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {!loading && consultado && pacientes.length === 0 ? (
              <p className="informes-muted">No hay pacientes para mostrar.</p>
            ) : null}
          </>
        ) : null}

        {vista === 'menu' && menu ? (
          <>
            <p>
              <strong>Paciente:</strong> {menu.estudio.paciente_nombre}
            </p>
            {especialidades.length > 1 ? (
              <label className="informes-field">
                <span>Tipo de estudio</span>
                <select
                  value={especialidadCodigo}
                  onChange={(event) => {
                    const cod = event.target.value;
                    setEspecialidadCodigo(cod);
                    setError('');
                    // Buscar estudio de esa rama para el mismo paciente.
                    buscarInformesPacientes(sessionUserId, cod, menu.estudio.paciente_nombre)
                      .then((data) => {
                        const lista = (data && data.pacientes) || [];
                        const mismo = lista.find(
                          (item) =>
                            Number(item.paciente_ficha_id) === Number(menu.estudio.paciente_ficha_id)
                        );
                        if (mismo) {
                          abrirMenu(mismo.estudio_id);
                        } else {
                          setError(
                            `El paciente no tiene estudios activos de esa especialidad con usted.`
                          );
                        }
                      })
                      .catch((err) => setError(err.message || 'No se pudo cambiar el tipo de estudio.'));
                  }}
                >
                  {especialidades.map((item) => (
                    <option key={item.codigo} value={item.codigo}>
                      {item.etiqueta || item.codigo}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="informes-muted">
                Tipo de estudio:{' '}
                <strong>{menu.estudio.especialidad_etiqueta || menu.estudio.especialidad_codigo}</strong>
              </p>
            )}

            <div className="informes-summary">
              <p>
                <strong>Días:</strong> {menu.estudio.dias_semana || menu.resumen.dias_semana || '—'}
              </p>
              <p>
                <strong>Inicio:</strong> {menu.estudio.fecha_inicio || menu.resumen.fecha_inicio || '—'}
              </p>
              <p>
                <strong>Citas pagadas:</strong> {menu.resumen.citas_pagadas} de {menu.minimo_plan}{' '}
                (mínimo para el Informe de Evaluación y Plan)
              </p>
            </div>

            <div className="informes-actions">
              <button type="button" className="informes-btn informes-btn--ghost" onClick={abrirTerapias}>
                Terapias pagadas
              </button>
              <button
                type="button"
                className="informes-btn informes-btn--ghost"
                onClick={abrirReportesDiarios}
              >
                Reportes diarios
              </button>
            </div>

            {menu.plantilla === false ? (
              <p className="informes-alert informes-alert--warn">
                Plantilla en desarrollo para {menu.estudio.especialidad_etiqueta || 'esta especialidad'}.
                Puede guardar un informe en texto libre mientras tanto.
              </p>
            ) : null}

            <h2>Tipos de Informe y Recomendaciones</h2>
            <div className="informes-option-list">
              {(menu.opciones || []).map((op, index) => (
                <button
                  key={op.tipo}
                  type="button"
                  className="informes-option"
                  disabled={!op.habilitada && !op.existe}
                  onClick={() => abrirEditor(op)}
                >
                  {index + 1}. {op.label}
                  {op.tipo === 'avance' ? ` (${op.avances || 0} de ${op.avances_max || 4})` : ''}
                  {op.existe && op.tipo !== 'avance' ? ' · Editar' : ''}
                  {op.motivo ? <small>{op.motivo}</small> : null}
                </button>
              ))}
              {(menu.proximamente || []).map((item, index) => (
                <div key={item.label} className="informes-option informes-option--soon">
                  {index + 4}. {item.label}
                </div>
              ))}
            </div>

            <div className="informes-actions">
              <button
                type="button"
                className="informes-btn informes-btn--secondary"
                onClick={() => abrirPendientes('sin_evaluacion')}
              >
                Pacientes sin Evaluación Inicial
              </button>
              <button
                type="button"
                className="informes-btn informes-btn--secondary"
                onClick={() => abrirPendientes('sin_plan')}
              >
                Pacientes sin Informe Evaluación y Plan
              </button>
            </div>
          </>
        ) : null}

        {vista === 'terapias' ? (
          <>
            <h2>Terapias pagadas</h2>
            <ul className="informes-list">
              {terapias.map((item) => (
                <li key={item.id}>
                  <div style={{ padding: '0.55rem 0.2rem', borderBottom: '1px solid #e5e7eb' }}>
                    {item.fecha} {item.hora}
                    {item.recibo_numero ? ` · Recibo ${item.recibo_numero}` : ''}
                  </div>
                </li>
              ))}
            </ul>
            {terapias.length === 0 ? <p className="informes-muted">Sin citas pagadas.</p> : null}
          </>
        ) : null}

        {vista === 'reportes' ? (
          <>
            <h2>Reportes diarios</h2>
            <ul className="informes-list">
              {reportes.map((item) => (
                <li key={`${item.cita_id}-${item.mensaje_id}`}>
                  <div style={{ padding: '0.55rem 0.2rem', borderBottom: '1px solid #e5e7eb' }}>
                    <strong>
                      {item.fecha} {item.hora}
                    </strong>
                    <p className="informes-muted" style={{ whiteSpace: 'pre-wrap' }}>
                      {item.cuerpo}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            {reportes.length === 0 ? (
              <p className="informes-muted">No hay reportes de cita (tipo reporte) en este estudio.</p>
            ) : null}
          </>
        ) : null}

        {vista === 'editor' ? (
          editorUsaPlantilla && editorDatos ? (
            <InformePlantillaForm
              titulo={editorTitulo || 'Informe'}
              pacienteNombre={(menu && menu.estudio && menu.estudio.paciente_nombre) || ''}
              secciones={seccionesPlantilla(editorTipo, editorRama, editorDatos)}
              datos={editorDatos}
              onChange={setEditorDatos}
              onGuardar={guardarEditor}
              onCancelar={() => setVista('menu')}
              guardando={loading}
            />
          ) : (
            <>
              <h2>{editorTitulo || 'Informe'}</h2>
              <p className="informes-muted">
                Plantilla estructurada aún no disponible para esta especialidad. Use texto libre.
              </p>
              <label className="informes-field">
                <span>Título</span>
                <input value={editorTitulo} onChange={(event) => setEditorTitulo(event.target.value)} />
              </label>
              <label className="informes-field">
                <span>Contenido</span>
                <textarea
                  value={editorCuerpo}
                  onChange={(event) => setEditorCuerpo(event.target.value)}
                  placeholder="Escriba el informe clínico…"
                />
              </label>
              <div className="informes-actions">
                <button
                  type="button"
                  className="informes-btn informes-btn--primary"
                  onClick={guardarEditor}
                  disabled={loading}
                >
                  Guardar informe
                </button>
                <button
                  type="button"
                  className="informes-btn informes-btn--ghost"
                  onClick={() => setVista('menu')}
                >
                  Cancelar
                </button>
              </div>
            </>
          )
        ) : null}
      </div>
    </div>
  );
};

export default InformesPage;
